import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { RadioButtonField } from '@sunwebgroup/uds-ui/fields/radio-button-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'

import { RadioButton, RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { createGroupsEvent } from '@/scopes/checkout/domain/analytics'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { FlexContent } from '@/scopes/checkout/groups/components/services/RoomSelection/FlexContent'
import styles from '@/scopes/checkout/groups/components/services/RoomSelection/RoomSelectionService.module.css'
import { isAnyRoomTypeAvailable } from '@/scopes/checkout/groups/components/services/RoomSelection/selectors'
import {
  getErrorMessage,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/getErrorMessages'
import {
  PinCodeFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/PinCodeFormField'
import { useAvailableRoomsQuery } from '@/scopes/checkout/groups/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

const newRoomOptionValue = '__new_room__'

export function SelectNamedRoomFormField() {
  const { data } = useAvailableRoomsQuery()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { rooms, roomTypes } = data || {}
  const intl = useIntl()
  const { formState, setValue, watch, control, clearErrors } = useFormContext()
  const { errors } = formState
  const isNewRoomSelected = watch('isNewRoom')
  const watchedAssignationMethod = watch('assignationMethod')
  const isAnyRoomTypeAvailableToCreateANamedRoom = isAnyRoomTypeAvailable(roomTypes)
  const { sendEvent } = useAnalytics()

  const { field } = useController({
    name: 'selectedRoomId',
    control,
    rules: {
      required: watchedAssignationMethod === 'specific' && !isNewRoomSelected,
    },
  })

  const handleRoomNameChange = (value: string) => {
    const isNewRoom = value === newRoomOptionValue

    sendEvent(
      createGroupsEvent({
        description: 'RoomSelection',
        element: 'radio button',
        type: 'selection',
        result: isNewRoom ? `new created room selected` : `already named room selected`,
      }),
    )

    field.onChange(value)
    setValue('pinCode', '')
    clearErrors('selectedRoomId')
    if (value === newRoomOptionValue) {
      setValue('isNewRoom', true)
      clearErrors('pinCode')
    }
    else {
      setValue('isNewRoom', false)
    }
  }

  if (!rooms) {
    return null
  }

  const errorMessage = getErrorMessage(
    'selectedRoomId',
    errors,
    { requiredErrorMessage: intl.formatMessage({ id: 'groups_checkout_steps_room_selection_named_room_required_error_message' }) },
  )

  return (
    <FormField
      label={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_named_room_field_label' })}
      feedbackText={errorMessage}
      status={errors.selectedRoomId ? 'error' : 'default'}
    >
      <RadioButtonGroup
        name={field.name}
        value={isNewRoomSelected ? newRoomOptionValue : field.value}
        onValueChange={handleRoomNameChange}
        onBlur={field.onBlur}
      >
        <FlexContent gap="primitives-space-12">
          <>
            {Object.values(rooms || {})
              .slice()
              .sort((a, b) => (a.isFull === b.isFull ? 0 : a.isFull ? 1 : -1))
              .map(room => (
                <React.Fragment key={room.roomId}>
                  <ListItem
                    variant="outlined"
                    as="label"
                    key={room.name}
                    className={cn(styles.listItemContainerLabel, room.isFull ? styles.isDisabled : '')}
                  >
                    <div className={cn(styles.radioButtonWithDescription)} style={{ display: 'flex', gap: '8px' }}>
                      <RadioButton value={room.roomId} disabled={room.isFull} />
                      <div className={styles.radioButtonDescription}>
                        <Typography variant="body-m-regular-tight">{room.name}</Typography>
                        {!room.isFull && (
                          <Typography variant="body-s-regular-tight" className={styles.availableRoomsAmount}>
                            {intl.formatMessage(
                              {
                                id:
                                    'groups_checkout_steps_room_selection_sheet_selection_named_room_quantity_available',
                              },
                              {
                                amount: room.availableBedsAmount,
                                total: room.totalBedsAmount,
                                type: room.description,
                              },
                            )}
                          </Typography>
                        )}
                        {room.isFull && (
                          <Typography variant="body-s-regular-tight" className={styles.availableRoomsAmount}>
                            {room.description}
                          </Typography>
                        )}
                      </div>
                      {room.isFull && (
                        <Tag variant="danger-tinted" size="M" className={styles.isFullTag}>
                          {intl.formatMessage({
                            id: 'groups_checkout_steps_room_selection_sheet_selection_named_room_full',
                          })}
                        </Tag>
                      )}
                    </div>
                  </ListItem>
                  {field.value === room.roomId
                  && !isNewRoomSelected
                  && bookingDraftData?.groupInformation?.pinCodeIsRequiredForRoomSelection && (
                    <PinCodeFormField
                      helpText={intl.formatMessage({
                        id: 'groups_checkout_steps_room_selection_named_room_pin_code_help_text',
                      })}
                    />
                  )}
                </React.Fragment>
              ))}
          </>
          {isAnyRoomTypeAvailableToCreateANamedRoom && (
            <RadioButtonField
              label={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_new_named_room_label' })}
              variant="outlined"
              value={newRoomOptionValue}
            />
          )}
        </FlexContent>
      </RadioButtonGroup>
    </FormField>
  )
}
